import React from 'react';
import { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import reactHtmlParser from 'react-html-parser';
import { Pencil } from '../shared/Pencil';
import { OpenFolder } from '../shared/OpenFolder';
import { PopOutIcon } from '../shared/PopOutIcon';
import { PlusSign } from '../shared/PlusSign';
import { EditFieldForm } from '../shared/EditFieldForm';
import { Checkbox, Popover } from '@salesforce/design-system-react';
import PropTypes from 'prop-types';
import { setDocrioFiles } from '../../actions/files';
import Svg from '../shared/Svg';
import { FileErrorIndicator } from './FileErrorIndicator';

import './DocrioFile.scss';

const DocrioFile = ({
  className,
  itemId,
  nameProp,
  imgSrc,
  editCallback,
  saveDispatchAction,
  savedState,
  isSaving,
  showFolder = false,
  showLocation = false,
  folderDisabled = false,
  showPopOut = false,
  autoSaving,
  folderPathObj = {},
  popoutLink,
  // redux
  files,
  emailId,
  draftId,
  objectsAndDisplayFields,
  selectedRecords,
  defaultFolderSettings,
  isComposingEmail,
  multiSave,
  docrioNamespace,
}) => {
  const dispatch = useDispatch();
  const saveAllEmailSettings = useSelector((state) => state.mail.settings?.docrioSettings?.emailFiles?.saveAllEmails);
  const validationErrors = useSelector((state) => state.files.fileValidationErrors?.[itemId]) || [];
  const [isEditingName, updateEditName] = useState(false);
  const [fileType, storeFileType] = useState('');
  const [name, updateName] = useState(nameProp);
  const [nameHover, setNameHover] = useState(false);
  const [toggleModal, setToggleModal] = useState(false);
  const isEml = itemId === emailId || itemId === draftId;
  const [relatedRecords, setRelatedRecords] = useState([]);
  const file = files.find((file) => file.id === itemId);
  const isSingleSelectedRecord = Object.keys(selectedRecords || {}).length === 1;

  // initialize the modal
  useEffect(() => {
    setRelatedRecords(Object.keys(files.find((file) => file.id === itemId).folderPaths || {}));
  }, [files]);

  const clickAction = () => {
    if (multiSave && !isSingleSelectedRecord) {
      setToggleModal((prev) => !prev);
    } else {
      handleApply();
    }
  };

  const toggleEditName = () => {
    // store the file type for later use
    if (name.includes('.') && file.contentType) {
      const splitName = name.split('.');
      storeFileType(splitName[splitName.length - 1]);
    }
    updateEditName(!isEditingName);
  };

  const editNameCallback = (newName) => {
    const splitName = newName.split('.');
    // if the user didn't include the file type, add it back
    if (fileType && splitName?.[splitName.length - 1] !== fileType) {
      newName = `${newName}.${fileType}`;
    }
    updateName(newName);
    updateEditName(false);
    storeFileType('');
    if (editCallback && typeof editCallback === 'function') {
      editCallback(newName, itemId);
    }
  };

  const handleChange = (e) => {
    if (e.target.checked) {
      setRelatedRecords([...relatedRecords, e.target.name]);
    } else {
      setRelatedRecords(relatedRecords.filter((record) => record !== e.target.name));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setRelatedRecords(Object.keys(selectedRecords));
    } else {
      setRelatedRecords(Object.keys(selectedRecords).filter((recordId) => isCheckBoxDisabled(recordId)));
    }
  };

  const handleApplyToAll = () => {
    // Create a new array of updated files
    const updatedFiles = files.map((file) => {
      // early return for docrio files during compose mode
      if (file.docrioId && isComposingEmail) {
        return file;
      }

      // Create a new folderPaths object for each file
      const isEmailFile = file.id === emailId || file.id === draftId;
      // early return if user has setting turned on to always save email file
      if (isEmailFile && saveAllEmailSettings) {
        return file;
      }
      const newFolderPathsObj = {};
      // make sure we don't deselect any disabled checkboxes
      Object.keys(selectedRecords).forEach((recordId) => {
        if (Array.isArray(selectedRecords[recordId]?.attachments) && selectedRecords[recordId]?.attachments?.find((attachment) => attachment?.[`${docrioNamespace}__File_Data_Hash__c`] === file.hash) && !isComposingEmail) {
          newFolderPathsObj[recordId] = file.folderPaths[recordId];
        }
      });

      relatedRecords.forEach((relatedRecordId) => {
        // Copy existing folder path or use default path
        if (file?.folderPaths[relatedRecordId]) {
          newFolderPathsObj[relatedRecordId] = file.folderPaths[relatedRecordId];
        } else {
          const defaultFolders = defaultFolderSettings[selectedRecords[relatedRecordId]?.type];

          if (isEmailFile && defaultFolders.saveEmailFolderPath !== '[]') {
            newFolderPathsObj[relatedRecordId] = { name: JSON.parse(defaultFolders?.saveEmailFolderPath || '[]')?.pop(), path: defaultFolders.saveEmailFolderPath, subFolders: [] };
          } else if (defaultFolders.saveAttachmentsFolderPath !== '[]') {
            newFolderPathsObj[relatedRecordId] = { name: JSON.parse(defaultFolders?.saveAttachmentsFolderPath || '[]')?.pop(), path: defaultFolders.saveAttachmentsFolderPath, subFolders: [] };
          } else {
            newFolderPathsObj[relatedRecordId] = { name: `${defaultFolders.objectLabel} Home`, path: '', subFolders: [] };
          }
        }
      });
      // Return a new file object with updated folderPaths
      return {
        ...file, // Spread the existing properties of the file
        folderPaths: { ...newFolderPathsObj }, // Spread the new folderPaths object to ensure immutability
        saved: false,
        toSave: true,
      };
    });

    // Dispatch a single action to update all files
    dispatch(setDocrioFiles(updatedFiles));

    // Close the modal
    setToggleModal(false);
  };

  // applies selected records to the current attachment
  const handleApply = (selectAllRecords = false) => {
    if (saveDispatchAction) {
      let records = relatedRecords;
      if (!multiSave || isSingleSelectedRecord) {
        if (file.folderPaths[Object.keys(selectedRecords)[0]]) { // user is toggling off the save
          records = [];
        } else {
          records = Object.keys(selectedRecords); // will only ever be one related record
        }
        setRelatedRecords(records);
      }
      if (selectAllRecords) {
        records = Object.keys(selectedRecords);
        setRelatedRecords(records);
      }
      // update the folderPaths for the selected records
      const newFolderPathsObj = {};
      records.forEach((record) => {
        if (folderPathObj[record]) {
          newFolderPathsObj[record] = folderPathObj[record];
        } else {
          const defaultFolders = defaultFolderSettings[selectedRecords[record]?.type];
          if (isEml && defaultFolders.saveEmailFolderPath !== '[]') {
            newFolderPathsObj[record] = { name: JSON.parse(defaultFolders?.saveEmailFolderPath || '[]')?.pop(), path: defaultFolders.saveEmailFolderPath, subFolders: [] };
          } else if (defaultFolders.saveAttachmentsFolderPath !== '[]') {
            newFolderPathsObj[record] = { name: JSON.parse(defaultFolders?.saveAttachmentsFolderPath || '[]')?.pop(), path: defaultFolders.saveAttachmentsFolderPath, subFolders: [] };
          } else {
            newFolderPathsObj[record] = { name: `${defaultFolders.objectLabel} Home`, path: '', subFolders: [] };
          }
        }
      });
      dispatch(saveDispatchAction(true, { itemId, name, folderPaths: newFolderPathsObj }));
    }
    setToggleModal(false);
  };

  const noRelatedRecords = !(file && file.folderPaths && Object.keys(file.folderPaths).length > 0);

  const getFolderName = () => {
    const firstValue = Object.values(folderPathObj || {})?.[0]?.name;
    // Handle files attached from docrio
    if (isComposingEmail && file.docrioId && firstValue) {
      return firstValue;
    }
    // check if all values are the same
    const objectTypes = Object.values(selectedRecords)?.map((record) => record.type);
    const uniqueObjectTypes = Array.from(new Set(objectTypes));
    // if multiple objects selected always return multiple
    if (uniqueObjectTypes.length > 1) {
      return 'Multiple';
    }

    let isTheSame = true;

    if (!firstValue) { // there won't be any values in folder path obj on inital load or if file is not selected to be saved so use default
      const objectType = objectTypes[0];
      const defaultFolders = defaultFolderSettings[objectType];
      const defaultFolderName = isEml ? JSON.parse(defaultFolders?.saveEmailFolderPath || '[]')?.pop() : JSON.parse(defaultFolders?.saveAttachmentsFolderPath || '[]')?.pop();

      return (noRelatedRecords && objectTypes.length > 1) && 'Multiple' || defaultFolderName || `${defaultFolders?.objectLabel} Home`;
    }

    Object.values(folderPathObj || {}).forEach((value) => {
      if (value?.name !== firstValue) {
        isTheSame = false;
      }
    });

    return isTheSame ? firstValue : 'Multiple';
  };

  const isCheckBoxDisabled = (recordId) => {
    // if the record is already saved, disable the checkbox
    const file = files.find((file) => file.id === itemId);
    // Files unpacked from a zip are matched by their Docrio file id, since two of them can share a hash.
    const isSameFile = (attachment) => (file.extractedFrom ?
      attachment?.[`${docrioNamespace}__File_Info__c`] === file.docrioId :
      attachment?.[`${docrioNamespace}__File_Data_Hash__c`] === file.hash);
    if (Array.isArray(selectedRecords[recordId]?.attachments) && selectedRecords[recordId]?.attachments?.find(isSameFile) && !isComposingEmail) {
      return true;
    }
    return false;
  };

  const isNewSaveLocationAdded = () => {
    // check if a file has a selected record added that is not saved in Docrio
    const selectedRecordIds = Object.keys(selectedRecords);
    for (let i = 0; i < selectedRecordIds?.length; i++) {
      if (!isCheckBoxDisabled(selectedRecords[selectedRecordIds[i]]?.Id) && relatedRecords.includes(selectedRecordIds[i])) {
        return true;
      }
    }
    return false;
  };

  const popoverContent = () => {
    return (
      <div className='docrioFileItem-popover'>
        <div className='docrioFileItem-popoverItem'>
          <Checkbox
            key='select-all'
            labels={{ label: 'Select All' }}
            name='select-all'
            errorText=''
            checked={Object.keys(relatedRecords).length === Object.keys(selectedRecords).length} // if all are selected
            indeterminate={Object.keys(relatedRecords).length > 0 && Object.keys(relatedRecords).length < Object.keys(selectedRecords).length} // if some are selected
            onChange={(e) => {
              handleSelectAll(e);
            }}
          />
        </div>
        {selectedRecords && Object.values(selectedRecords)?.map((record) => {
          const currentDisplayFields = objectsAndDisplayFields?.find((object) => object.apiName === record.type)?.displayFields.map((field) => field.apiName) || [];
          return (
            <div key={record.Id} className='docrioFileItem-popoverItem'>
              <Checkbox
                key={ record?.[currentDisplayFields[0]] }
                labels={{ label: reactHtmlParser(record?.[currentDisplayFields[0]] || '') }}
                name={record.Id}
                checked={relatedRecords.includes(record.Id)}
                onChange={(e) => {
                  handleChange(e);
                }}
                disabled={isCheckBoxDisabled(record.Id)}
              />
            </div>
          );
        })}
        <div className='docrioFileItem-popoverFooter'>
          <button onClick={() => handleApply()}>Apply</button>
          <p onClick={handleApplyToAll}>Apply to All</p>
        </div>
      </div>
    );
  };

  const getLocation = () => {
    if (isComposingEmail && file.docrioId && Object.keys(file?.folderPaths || {})?.[0]) {
      const record = selectedRecords[Object.keys(file.folderPaths)[0]];
      const currentDisplayFields = objectsAndDisplayFields?.find((object) => object.apiName === record?.type)?.displayFields.map((field) => field.apiName) || [];
      return record[currentDisplayFields[0]] || '';
    }
    return `${Object.keys(file?.folderPaths || {}).length}/${Object.keys(selectedRecords || {}).length}`;
  };

  useEffect(() => {
    updateName(nameProp);
  }, [nameProp]);

  return (
    <div className={`docrioFileItem-container ${className}`}>
      <div className='docrioFileItem-imageWrapper'>
        <FileErrorIndicator itemId={itemId} messages={validationErrors} />
        <Svg className="docrioFileItem-image" data={imgSrc}/>
      </div>
      <div className='docrioFileItem-details'>
        <EditFieldForm
          fieldProp={name}
          isEditingFieldProp={isEditingName}
          editingFieldCallback={editNameCallback}
          hoverCallback={setNameHover}
        />
        {showLocation && <p className='docrioFileItem-location'><b>Location: </b> {reactHtmlParser(getLocation() || '')}</p>}
        { (popoutLink && typeof folderPathObj === 'string' && !savedState) ? <p className='docrioFileItem-attachedFrom'><b>Attached From: </b>{folderPathObj || ''}</p> : // TODO update in next ticket https://litify-central.atlassian.net/browse/EM-1999
          (showFolder && folderPathObj) && <p className='docrioFileItem-folder'><b>Folder: </b>{ getFolderName() }</p>}
      </div>
      <div className='docrioFileItem-iconsContainer'>
        {/* There is only one instance where we do not render the pencil and it is if it attached from docrio, implicitly determined by popout. */}
        {(!isEditingName && !showPopOut && !savedState && !isSaving) &&
          <Pencil isOuterHovered={nameHover} onClickCallback={toggleEditName} tooltipContent='Edit name'/>
        }
        { (showFolder && !isSaving && !showPopOut) &&
          <OpenFolder itemId={itemId} disabled={folderDisabled} clickAction={(relatedRecords?.length === 0 || !isNewSaveLocationAdded()) ? handleApply : null}/>
        }
        { showPopOut ?
        <PopOutIcon
          link={!Array.isArray(popoutLink) && popoutLink}
          linkArr={Array.isArray(popoutLink) && popoutLink}
          className={'docrioFileItem-removeMargin'}
          isDocrio
        /> :
        <Popover
          body={toggleModal ? popoverContent() : null}
          id={`select-saved-location-${itemId}`}
          isOpen={toggleModal}
          style={{ width: '200px' }}
          align='top right'
          onRequestClose={() => setToggleModal(false)}
          hasNoCloseButton
          hasNoNubbin
          overlay
        >
          <PlusSign
            clickAction={clickAction}
            hovering={false}
            // if set for auto save, we want to grey it out and no present the option to not save.
            savedState={savedState || autoSaving || isSaving}
            selectedState={Object.keys(folderPathObj).length === Object.keys(selectedRecords).length}
            partialSelection={Object.keys(folderPathObj).length > 0 && Object.keys(folderPathObj).length < Object.keys(selectedRecords).length}
            isDocrio
          />
        </Popover>
        }
      </div>
    </div>
  );
};

DocrioFile.propTypes = {
  className: PropTypes.string,
  itemId: PropTypes.string,
  nameProp: PropTypes.string,
  imgSrc: PropTypes.string.isRequired,
  editCallback: PropTypes.func,
  saveDispatchAction: PropTypes.func,
  savedState: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  showFolder: PropTypes.bool,
  showLocation: PropTypes.bool,
  folderDisabled: PropTypes.bool,
  showPopOut: PropTypes.bool,
  selected: PropTypes.bool,
  autoSaving: PropTypes.bool,
  folderPathObj: PropTypes.object,
  popoutLink: PropTypes.string,
  // redux
  files: PropTypes.array,
  emailId: PropTypes.string,
  draftId: PropTypes.string,
  objectsAndDisplayFields: PropTypes.array,
  selectedRecords: PropTypes.object,
  defaultFolderSettings: PropTypes.object,
  isComposingEmail: PropTypes.bool,
  multiSave: PropTypes.bool,
  docrioNamespace: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    files: state.files.docrioFiles,
    emailId: state.mail.activeEmail,
    draftId: state.mail.draftId,
    objectsAndDisplayFields: state.mail.settings?.objectsAndDisplayFields,
    selectedRecords: state.mail.selectedRecords,
    defaultFolderSettings: state.mail.settings.docrioSettings.defaultFolderSettings,
    isComposingEmail: state.mail.isComposingEmail,
    multiSave: state.mail.settings.multiSave,
    docrioNamespace: state.mail.settings?.override?.docrioNamespace || 'litify_docs',
  };
};

const DocrioFileWithState = connect(mapStateToProps)(DocrioFile);
export default DocrioFileWithState;
